import React from 'react';
import {
  DefaultCard,
  H4Text,
  SubTitle1Text,
  SubTitle2Text,
  colors,
  moneyFormat,
} from 'design-system';
import { Box, Grid, Typography, makeStyles } from '@material-ui/core';
import { LegalBeneficiary } from 'src/models/helperTypes/EmissionDataFields';
import {
  CoverageCatalog,
  Quotation,
  QuotationCoverage,
} from 'data-layer-cr/dist/models';
import {
  endorsementCoverages,
  fullDataForCode,
} from 'data-layer-cr/dist/functions/coverages';

const { formatMoney } = moneyFormat;

const useStyles = makeStyles({
  fieldLabel: {
    color: colors.abbey,
    fontSize: 14,
    marginTop: '1rem',
  },
  logo: { marginRight: 11 },
  infoLogo: { marginRight: 5 },
  noteContainer: { marginTop: '1rem' },
  noteLabel: { fontSize: 13 },
  beneficiariesLabel: { fontSize: 17 },
  nameContainer: { minWidth: '25%' },
  rateContainer: { paddingLeft: 25 },
});

export const noBeneficiariesLabel =
  'Esta póliza será emitida con herederos legales como beneficiarios del seguro de vida accidental. ';
export const freeMaxCost = 'Gratis';

type Props = {
  legalBeneficiaries?: LegalBeneficiary[];
  coverageCatalogs?: CoverageCatalog[];
  quotation?: Quotation;
  productEndorsements?: QuotationCoverage[];
};

const BeneficiariesDataCard = ({
  legalBeneficiaries = [],
  coverageCatalogs = [],
  quotation,
  productEndorsements,
}: Props): JSX.Element => {
  const classes = useStyles();

  const endorsementList = endorsementCoverages(coverageCatalogs);
  return (
    <DefaultCard
      data-testid="beneficiaries-data-card"
      title={
        <Grid item>
          <Box display="flex" mb={2}>
            <img
              src="svg/heart.svg"
              alt="heart_icon"
              className={classes.logo}
              data-testid="icon-image"
            />
            <SubTitle1Text>Beneficios incluidos en la póliza</SubTitle1Text>
          </Box>
        </Grid>
      }
      collapsible
    >
      <Grid container direction="column">
        {productEndorsements?.map((endorsement) => {
          const currentEndorsement = fullDataForCode(
            endorsementList,
            endorsement.coverageId,
          );

          if (!currentEndorsement) {
            return null;
          }
          return (
            <Grid item data-testid="endorsement-item">
              <Typography
                data-testid={`name-${endorsement.coverageId}`}
                variant="caption"
                className={classes.fieldLabel}
              >
                {currentEndorsement.name}
              </Typography>
              <SubTitle1Text data-testid={`cost-${endorsement.coverageId}`}>
                {!currentEndorsement.maxCost
                  ? freeMaxCost
                  : formatMoney(
                      currentEndorsement.maxCost,
                      false,
                      quotation?.quotationProfile.typeOfMoney,
                    )}
              </SubTitle1Text>
            </Grid>
          );
        })}

        {legalBeneficiaries.length > 0 && (
          <>
            <Typography variant="caption" className={classes.fieldLabel}>
              Beneficiarios de la póliza de vida accidental
            </Typography>
            <Grid
              container
              wrap="nowrap"
              direction="row"
              justifyContent="flex-start"
            >
              <table>
                {legalBeneficiaries.map(
                  ({ firstName, lastName, participationRate, id }) => (
                    <tr key={id}>
                      <td className={classes.nameContainer}>
                        <SubTitle2Text
                          className={classes.beneficiariesLabel}
                          data-testid="text-full-name"
                        >
                          {firstName} {lastName}
                        </SubTitle2Text>
                      </td>
                      <td className={classes.rateContainer}>
                        <SubTitle2Text
                          className={classes.beneficiariesLabel}
                          data-testid="text-participationRate"
                        >
                          {participationRate}%
                        </SubTitle2Text>
                      </td>
                    </tr>
                  ),
                )}
              </table>
            </Grid>
          </>
        )}

        <Grid
          container
          direction="row"
          wrap="nowrap"
          alignItems="center"
          className={classes.noteContainer}
        >
          <img
            src="images/info_outlined.svg"
            alt="info_icon"
            className={classes.infoLogo}
            data-testid="icon-image"
          />

          <H4Text data-testid="info-text" className={classes.noteLabel}>
            {legalBeneficiaries.length === 0 && noBeneficiariesLabel}
            Recuerda que los beneficiarios podrán ser modificados en cualquier
            momento posterior a la emisión.
          </H4Text>
        </Grid>
      </Grid>
    </DefaultCard>
  );
};

export default BeneficiariesDataCard;
